/**
 * Phase 7.0-lite P0-b — Client-side behavioral event logger.
 *
 * Fire-and-forget POSTs to /api/events/log. Never throws to the caller.
 * Includes a tiny debounce so e.g. quickly opening/closing a modal doesn't
 * spam the warehouse with product.viewed events.
 *
 * Usage from any React handler:
 *   import { logEvent } from '../utils/eventsLogger';
 *   logEvent('cart.add', { product_id: p.id, name: p.name, qty });
 */
import axios from 'axios';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

// 30-second debounce window per (event_type + dedupe_key)
const _recentlySent = new Map();
const DEBOUNCE_MS = 30_000;

// Stable session id for the lifetime of this tab — anchors view→cart→checkout journeys
let _sessionId = null;
function getSessionId() {
  if (_sessionId) return _sessionId;
  try {
    const cached = sessionStorage.getItem('am.events.session_id');
    if (cached) {
      _sessionId = cached;
      return cached;
    }
  } catch (_e) { /* sessionStorage unavailable */ }
  _sessionId = `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  try { sessionStorage.setItem('am.events.session_id', _sessionId); } catch (_e) { /* ignore */ }
  return _sessionId;
}

/**
 * Log a behavioral event. Best-effort — silent failure.
 *
 * @param {string} eventType  e.g. 'product.viewed', 'cart.add', 'cart.remove'
 * @param {object} payload    event-specific JSON-serializable data
 * @param {object} [opts]
 * @param {string} [opts.dedupeKey] if provided, identical (eventType + dedupeKey)
 *                                  posts within DEBOUNCE_MS are suppressed.
 */
export function logEvent(eventType, payload = {}, opts = {}) {
  try {
    const key = opts.dedupeKey ? `${eventType}:${opts.dedupeKey}` : null;
    if (key) {
      const last = _recentlySent.get(key);
      if (last && Date.now() - last < DEBOUNCE_MS) return;
      _recentlySent.set(key, Date.now());
    }
    // Lazy POST — don't block, don't await, don't toast errors.
    axios.post(
      `${API_URL}/api/events/log`,
      {
        event_type: eventType,
        payload: payload || {},
        session_id: getSessionId(),
      },
      { withCredentials: true, timeout: 5000 }
    ).catch(() => { /* swallow — analytics must never break UX */ });
  } catch (_e) {
    /* swallow */
  }
}
